import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { UserPlus, ShieldCheck, KeyRound, Ban, CheckCircle2, Mail, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Spinner } from '@/components/ui/spinner';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { useAuthStore } from '@/stores/authStore';
import api from '@/lib/axios';
import { API_ENDPOINTS } from '@/constants/api-endpoints';

interface Admin {
  id: string;
  name: string;
  email: string;
  is_super_admin: boolean;
  is_active: boolean;
  must_change_password: boolean;
  created_at: string;
}

const fmtDate = (d: string) => new Date(d).toLocaleDateString('en-IN', { dateStyle: 'medium' });
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function TeamManagementTab() {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Admin | null>(null);

  const { data: admins = [], isLoading } = useQuery({
    queryKey: ['admin-team'],
    queryFn: async (): Promise<Admin[]> => {
      const res = await api.get(API_ENDPOINTS.ADMIN_TEAM.LIST);
      const raw = (res.data as any)?.data ?? res.data;
      return Array.isArray(raw) ? raw : [];
    },
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['admin-team'] });

  const createAdmin = useMutation({
    mutationFn: async () => {
      const res = await api.post(API_ENDPOINTS.ADMIN_TEAM.CREATE, { name: name.trim(), email: email.trim() });
      return res.data?.data ?? res.data;
    },
    onSuccess: (data: any) => {
      toast.success(data?.message || 'Admin created — temp password emailed.');
      setName(''); setEmail(''); setFormError(null);
      invalidate();
    },
    onError: (e: any) => setFormError(e?.response?.data?.message || 'Could not create admin.'),
  });

  const setStatus = useMutation({
    mutationFn: ({ id, is_active }: { id: string; is_active: boolean }) =>
      api.put(API_ENDPOINTS.ADMIN_TEAM.STATUS(id), { is_active }),
    onSuccess: (_d, v) => { toast.success(v.is_active ? 'Admin enabled' : 'Admin disabled'); invalidate(); },
    onError: (e: any) => toast.error(e?.response?.data?.message || 'Update failed'),
  });

  const resetPassword = useMutation({
    mutationFn: (id: string) => api.post(API_ENDPOINTS.ADMIN_TEAM.RESET_PASSWORD(id)),
    onSuccess: (res: any) => toast.success(res?.data?.data?.message || res?.data?.message || 'New temp password emailed.'),
    onError: (e: any) => toast.error(e?.response?.data?.message || 'Reset failed'),
  });

  const deleteAdmin = useMutation({
    mutationFn: (id: string) => api.delete(API_ENDPOINTS.ADMIN_TEAM.DELETE(id)),
    onSuccess: (res: any) => {
      toast.success(res?.data?.data?.message || res?.data?.message || 'Admin deleted.');
      setDeleteTarget(null);
      invalidate();
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || 'Delete failed'),
  });

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    if (name.trim().length < 2) { setFormError('Enter the admin\'s full name.'); return; }
    if (!EMAIL_RE.test(email.trim())) { setFormError('Enter a valid email address.'); return; }
    createAdmin.mutate();
  };

  return (
    <div className="space-y-6">
      {/* Invite */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5" />
            Add Administrator
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Creates an admin account and emails them a temporary password. They'll set their own
            password (verified by email) on first login.
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleCreate} className="space-y-4 max-w-xl">
            {formError && (
              <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-lg">{formError}</div>
            )}
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="adminName" required>Full Name</Label>
                <Input id="adminName" placeholder="e.g. Priya Sharma" value={name} onChange={(e) => setName(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="adminEmail" required>Email Address</Label>
                <Input id="adminEmail" type="email" placeholder="name@ridezipto.com"
                  leftIcon={<Mail className="h-4 w-4" />}
                  value={email} onChange={(e) => setEmail(e.target.value)} />
              </div>
            </div>
            <Button type="submit" isLoading={createAdmin.isPending}>
              <UserPlus className="h-4 w-4 mr-2" /> Create &amp; Send Invite
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5" />
            Administrators
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex justify-center py-12"><Spinner /></div>
          ) : !admins.length ? (
            <div className="text-center py-12 text-muted-foreground">No administrators found.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Added</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {admins.map((a) => {
                  const isSelf = a.id === user?.id;
                  return (
                    <TableRow key={a.id}>
                      <TableCell className="font-medium">
                        {a.name}{isSelf && <span className="text-xs text-muted-foreground ml-1">(you)</span>}
                      </TableCell>
                      <TableCell className="text-sm">{a.email}</TableCell>
                      <TableCell>
                        {a.is_super_admin
                          ? <Badge className="bg-violet-100 text-violet-700">Super Admin</Badge>
                          : <Badge variant="outline">Admin</Badge>}
                      </TableCell>
                      <TableCell>
                        {!a.is_active
                          ? <Badge className="bg-red-100 text-red-700">Disabled</Badge>
                          : a.must_change_password
                            ? <Badge className="bg-amber-100 text-amber-700">Pending first login</Badge>
                            : <Badge className="bg-emerald-100 text-emerald-700">Active</Badge>}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">{fmtDate(a.created_at)}</TableCell>
                      <TableCell className="text-right">
                        {a.is_super_admin || isSelf ? (
                          <span className="text-xs text-muted-foreground">—</span>
                        ) : (
                          <div className="flex justify-end gap-2">
                            <Button size="sm" variant="outline"
                              onClick={() => resetPassword.mutate(a.id)}
                              disabled={resetPassword.isPending}>
                              <KeyRound className="h-4 w-4 mr-1" /> Reset
                            </Button>
                            {a.is_active ? (
                              <Button size="sm" variant="outline" className="text-red-600"
                                onClick={() => setStatus.mutate({ id: a.id, is_active: false })}
                                disabled={setStatus.isPending}>
                                <Ban className="h-4 w-4 mr-1" /> Disable
                              </Button>
                            ) : (
                              <Button size="sm" variant="outline" className="text-emerald-600"
                                onClick={() => setStatus.mutate({ id: a.id, is_active: true })}
                                disabled={setStatus.isPending}>
                                <CheckCircle2 className="h-4 w-4 mr-1" /> Enable
                              </Button>
                            )}
                            <Button size="sm" variant="outline" className="text-red-600"
                              onClick={() => setDeleteTarget(a)}>
                              <Trash2 className="h-4 w-4 mr-1" /> Delete
                            </Button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => { if (deleteTarget) deleteAdmin.mutate(deleteTarget.id); }}
        title="Delete Administrator?"
        description={`This permanently removes ${deleteTarget?.name}'s admin account (${deleteTarget?.email}). This cannot be undone.`}
        confirmText="Delete"
        variant="destructive"
        isLoading={deleteAdmin.isPending}
      />
    </div>
  );
}
